import { useMemo } from 'react';
import { up } from 'styled-breakpoints';
import styled, { css } from 'styled-components';
import { match } from 'ts-pattern';
import Calendar from 'src/components/general/calendar/calendar';
import { Icon } from 'src/components/general/icon';
import { DatePickerMode } from 'src/components/general/inputs/custom-date-pickers/lgg-date-time-picker';
import { FlexRow } from 'src/components/layout/flex-row';
import { useDateHelpers } from 'src/hooks/use-date-helpers';

export const DateBox = styled(FlexRow)`
  align-items: center;
  height: 26px;
  justify-content: center;
  margin: 0 auto;
  width: 26px;
`;

const ChangeMonthIcon = styled(Icon)`
  position: relative;

  svg {
    height: 18px;
    width: 18px;

    path {
      fill: ${({ theme }) => theme.colors.flint};
    }
  }
`;

const CalendarHeaderContainer = styled(FlexRow)`
  align-items: center;
  border-bottom: 1px solid ${({ theme }) => theme.colors.koala};
  border-top: 1px solid ${({ theme }) => theme.colors.koala};
  height: 40px;
  padding: 4px 18px;
  user-select: none;
  width: 100%;

  ${up('md')} {
    border-top: none;
    height: 36px;
  }
`;

const CalendarHeaderDate = styled.span`
  color: ${({ theme }) => theme.colors.smalt};
  display: inline-block;
  font-family: ${({ theme }) => theme.font.medium};
  font-size: 15px;
  text-align: center;
  text-transform: capitalize;
  width: 100%;

  &:first-child {
    margin-left: 17px;
  }

  &:last-child {
    margin-right: 17px;
  }
`;

export const CalendarDateItem = styled.span`
  display: inline-block;
  width: 100%;
`;

const selectedDateStyles = css`
  background: ${({ theme }) => theme.colors.gogo};
  color: white;
  position: relative;
  z-index: 2;
`;

const selectedDateRangeStyles = css`
  background: rgba(163, 223, 236, 0.2);
  content: '';
  display: block;
  height: 26px;
  position: absolute;
  top: 4px;
  width: 50%;
  z-index: 1;
`;

export const StyledCalendar = styled(Calendar)`
  font-family: ${({ theme }) => theme.font.regular};

  .ant-picker-cell::before {
    display: none;
  }

  &.ant-picker-calendar {
    ${up('md')} {
      margin: 5px 15px 5px 15px;
    }

    .ant-picker-panel {
      ${up('md')} {
        border-top: none;
      }

      .ant-picker-body {
        padding: 0 0 33px;

        ${up('md')} {
          padding: 0;
        }

        .ant-picker-content {
          height: 231px;
        }

        thead {
          height: 30px;
          text-transform: capitalize;

          tr {
            th {
              color: ${({ theme }) => theme.colors.steel};
              font-size: 14px;
              padding-bottom: 5px;
              text-align: center;
            }
          }
        }

        tbody {
          font-size: 15px;
          height: max-content;

          tr {
            height: 26px;
          }

          .ant-picker-cell {
            color: ${({ theme }) => theme.colors.casper};
            padding: 0;

            &.ant-picker-cell-in-view {
              color: ${({ theme }) => theme.colors.smalt};

              &:not(.ant-picker-cell-disabled) {
                &.ant-picker-cell-today {
                  ${DateBox} {
                    ${selectedDateStyles};
                    background: ${({ theme }) => theme.colors.porcelain};
                    border-radius: 6px;
                    color: ${({ theme }) => theme.colors.smalt};
                  }
                }
              }

              ${CalendarDateItem} {
                &.start-date {
                  ${DateBox} {
                    ${selectedDateStyles};
                    border-radius: 6px 0 0 6px;
                  }
                }

                &.end-date {
                  ${DateBox} {
                    ${selectedDateStyles};
                    border-radius: 0 6px 6px 0;
                  }
                }

                &.start-date.end-date,
                &.selected-date {
                  ${DateBox} {
                    ${selectedDateStyles};
                    border-radius: 6px;
                  }
                }

                &.show-range {
                  &.in-range:not(.start-date):not(.end-date) {
                    background: rgba(163, 223, 236, 0.2);
                    width: 100%;
                  }

                  &.start-date {
                    ::before {
                      ${selectedDateRangeStyles};
                      left: 50%;
                    }
                  }

                  &.end-date {
                    ::before {
                      ${selectedDateRangeStyles};
                      right: 50%;
                    }
                  }
                }
              }
            }

            &.ant-picker-cell-disabled {
              color: ${({ theme }) => theme.colors.casper};
            }
          }
        }
      }
    }
  }
`;

const CalendarHeaderElement = styled.span<{ $allowModeChange: boolean }>`
  ${({ $allowModeChange }) =>
    $allowModeChange &&
    css`
      cursor: pointer;

      &:hover {
        color: ${({ theme }) => theme.colors.gogo};
      }
    `}
`;

const CalendarHeaderContent = styled(FlexRow)`
  align-items: center;
  gap: 5px;
  justify-content: center;
`;

const CALENDAR_HEADER_MONTH_FORMAT = 'MMM';
const CALENDAR_HEADER_YEAR_FORMAT = 'YYYY';

const getDecadeRange = (year: number): [number, number] => {
  const start = Math.floor(year / 10) * 10;
  return [start, start + 9];
};

type CalendarHeaderProps = {
  onNext?: VoidFunction;
  onBack?: VoidFunction;
  headerDate: Date;
  baseTestId?: string;
  mode?: DatePickerMode;
} & (
  | {
      allowModeChange: true;
      onChangeMode: (mode: DatePickerMode) => void;
    }
  | {
      allowModeChange: false;
      onChangeMode?: undefined;
    }
);

export const CalendarHeader = ({
  onNext,
  onBack,
  headerDate,
  baseTestId,
  mode,
  onChangeMode,
  allowModeChange = true,
}: CalendarHeaderProps) => {
  const { format } = useDateHelpers();

  const monthContent = useMemo(() => {
    const content = format(headerDate, CALENDAR_HEADER_MONTH_FORMAT);

    return (
      <CalendarHeaderElement
        $allowModeChange={allowModeChange}
        onClick={() => onChangeMode?.('year')}
        data-lgg-id={`${baseTestId}-picker-change-current-month-trigger`}
      >
        {content}
      </CalendarHeaderElement>
    );
  }, [baseTestId, format, headerDate, onChangeMode, allowModeChange]);

  const yearContent = useMemo(() => {
    const content = format(headerDate, CALENDAR_HEADER_YEAR_FORMAT);
    return (
      <CalendarHeaderElement
        $allowModeChange={allowModeChange}
        onClick={() => onChangeMode?.('decade')}
        data-lgg-id={`${baseTestId}-picker-change-current-year-trigger`}
      >
        {content}
      </CalendarHeaderElement>
    );
  }, [baseTestId, format, headerDate, onChangeMode, allowModeChange]);

  const content = useMemo(() => {
    return match(mode)
      .with('decade', () => {
        const [startYear, endYear] = getDecadeRange(headerDate.getFullYear());

        return (
          <CalendarHeaderContent onClick={() => onChangeMode?.('year')}>
            <span>{`${startYear} - ${endYear}`}</span>
          </CalendarHeaderContent>
        );
      })
      .otherwise(() => (
        <CalendarHeaderContent>
          {monthContent}
          {yearContent}
        </CalendarHeaderContent>
      ));
  }, [headerDate, mode, monthContent, onChangeMode, yearContent]);

  return (
    <CalendarHeaderContainer>
      {onBack ? (
        <ChangeMonthIcon
          type="arrowBackNoPadding"
          onClick={onBack}
          data-lgg-id={`${baseTestId}-picker-prev-month-button`}
        />
      ) : null}
      <CalendarHeaderDate data-lgg-id={`${baseTestId}-picker-current-month-title`}>
        {content}
      </CalendarHeaderDate>
      {onNext ? (
        <ChangeMonthIcon
          type="arrowNextNoPadding"
          onClick={onNext}
          data-lgg-id={`${baseTestId}-picker-next-month-button`}
        />
      ) : null}
    </CalendarHeaderContainer>
  );
};
